import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package } from "lucide-react";

let opener = null;

// Verifica gli articoli estratti: quelli non in catalogo vanno creati, associati o saltati. Ritorna null se annullato.
export const reviewMagazzinoItems = async (items) => {
  if (!items?.length) return items;
  const { data } = await api.post("/magazzino/match-items", { items });
  const rows = data.items.map((r) => ({ ...r, magazzino_id: r.match?.id || null }));
  if (rows.every((r) => r.match) || !opener) return rows.map(({ match, ...r }) => r);
  return opener(rows);
};

export const MagazzinoReview = () => {
  const [rows, setRows] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const resolveRef = useRef(null);

  useEffect(() => {
    opener = (r) => new Promise((resolve) => {
      resolveRef.current = resolve;
      setRows(r.map((x) => ({ ...x, azione: x.match ? "ok" : "nuovo" })));
      api.get("/magazzino").then(({ data }) => setCatalogo(data || [])).catch(() => setCatalogo([]));
    });
    return () => { opener = null; };
  }, []);

  const finish = (ok) => {
    const out = ok ? rows.map(({ match, azione, ...r }) => ({ ...r, skip: azione === "salta" || undefined, magazzino_id: azione === "esistente" || azione === "ok" ? r.magazzino_id : null })) : null;
    resolveRef.current?.(out);
    resolveRef.current = null;
    setRows(null);
  };
  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const categorie = [...new Set(catalogo.map((c) => c.categoria).filter(Boolean))];

  return (
    <Dialog open={!!rows} onOpenChange={(o) => { if (!o && rows) finish(false); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="magazzino-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Articoli non presenti in magazzino</DialogTitle>
          <DialogDescription>Per ogni articolo nuovo scegli se crearlo, associarlo a uno esistente o non caricarlo.</DialogDescription>
        </DialogHeader>
        <datalist id="mag-categorie">{categorie.map((c) => <option key={c} value={c} />)}</datalist>
        <div className="border rounded-md divide-y">
          {(rows || []).map((r, i) => <RigaArticolo key={i} r={r} i={i} setRow={setRow} catalogo={catalogo} />)}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => finish(false)} data-testid="magazzino-review-cancel">Annulla salvataggio</Button>
          <Button onClick={() => finish(true)} data-testid="magazzino-review-confirm">Conferma e salva</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const RigaArticolo = ({ r, i, setRow, catalogo }) => (
  <div className="p-3 text-sm space-y-2" data-testid={`magazzino-review-row-${i}`}>
    <div className="flex flex-wrap justify-between gap-2">
      <div><b>{r.nome}</b> · {r.quantita} {r.unita_misura} {r.codice ? `· cod. ${r.codice}` : ""}</div>
      {r.match ? <span className="text-xs text-emerald-700">In catalogo: {r.match.nome} (giacenza {r.match.quantita})</span> : (
        <Select value={r.azione} onValueChange={(v) => setRow(i, { azione: v })}>
          <SelectTrigger className="w-56 h-8 text-xs" data-testid={`magazzino-review-action-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="nuovo">Crea nuovo articolo</SelectItem>
            <SelectItem value="esistente">Associa a un articolo esistente</SelectItem>
            <SelectItem value="salta">Non caricare</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
    {!r.match && r.azione === "nuovo" && (
      <Input list="mag-categorie" className="h-8 text-xs" placeholder="Categoria (es. Cementi, Ferro)" value={r.categoria || ""}
             onChange={(e) => setRow(i, { categoria: e.target.value })} data-testid={`magazzino-review-categoria-${i}`} />
    )}
    {!r.match && r.azione === "esistente" && (
      <Select value={r.magazzino_id || ""} onValueChange={(v) => setRow(i, { magazzino_id: v })}>
        <SelectTrigger className="h-8 text-xs" data-testid={`magazzino-review-existing-${i}`}><SelectValue placeholder="Scegli articolo" /></SelectTrigger>
        <SelectContent>
          {catalogo.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} {c.categoria ? `· ${c.categoria}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
    )}
  </div>
);
